import { LocationStrategy } from '@angular/common';
import {
  Directive,
  ElementRef,
  Renderer2,
  effect,
  inject,
  input,
  ɵɵsanitizeUrlOrResourceUrl,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LanguageService } from '@pyg-services/language/language.service';

@Directive({
  selector: '[langRouterLink]',
  standalone: true,
})
export class LangRouterLink extends RouterLink {
  readonly langRouterLink = input<typeof this.routerLink>();
  private readonly langService = inject(LanguageService);
  private readonly langRouter: Router;
  private readonly langRenderer: Renderer2;
  private readonly langEl: ElementRef<HTMLElement>;
  private readonly langLocationStrategy: LocationStrategy;

  constructor() {
    const router = inject(Router);
    const route = inject(ActivatedRoute);
    const renderer = inject(Renderer2);
    const el = inject(ElementRef);
    const locationStrategy = inject(LocationStrategy);

    super(router, route, null, renderer, el, locationStrategy);

    this.langRouter = router;
    this.langRenderer = renderer;
    this.langEl = el;
    this.langLocationStrategy = locationStrategy;

    effect(() => {
      const tag = this.langEl.nativeElement.tagName.toLowerCase();
      const lang = this.langService.activeLanguage();
      const path = this.langRouterLink();
      const value = Array.isArray(path) ? path : [path];
      const fullLink = ['/', lang, ...value];
      this.routerLink = fullLink;

      if (tag === 'a') {
        this.addHref();
      }
    });
  }

  private addHref() {
    const urlTree = this.urlTree;
    this.href =
      urlTree !== null && this.langLocationStrategy
        ? this.langLocationStrategy?.prepareExternalUrl(
            this.langRouter.serializeUrl(urlTree),
          )
        : null;

    const sanitizedValue =
      this.href === null
        ? null
        : ɵɵsanitizeUrlOrResourceUrl(
            this.href,
            this.langEl.nativeElement.tagName.toLowerCase(),
            'href',
          );

    this.addAttributeValue('href', sanitizedValue);
  }

  private addAttributeValue(attrName: string, attrValue: string | null) {
    const nativeElement = this.langEl.nativeElement;

    if (attrValue !== null) {
      this.langRenderer.setAttribute(nativeElement, attrName, attrValue);
    } else {
      this.langRenderer.removeAttribute(nativeElement, attrName);
    }
  }
}
